/**
 * useLiveReportsData — fetch / refresh / load-more for parent Live Reports.
 * Owns the page cursor (synced from API `meta`). Mapping lives in the view-model;
 * interactions that only need fetch live here (not in useLiveReportsState).
 */
import { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import { apiIdle, apiRequest } from "../../../../../../data/redux/actions/api"
import { liveReportService } from "../../../../../../data/services/liveReportService"

type ApiSlice = {
    request?: boolean
    success?: boolean
    error?: unknown
    data?: { meta?: { current_page?: number; last_page?: number; total?: number } }
}

/** Related pagination fields — one atomic object, not sibling useStates. */
type PageCursor = {
    currentPage: number
    lastPage: number
    totalCount: number
}

const INITIAL_CURSOR: PageCursor = {
    currentPage: 1,
    lastPage: 1,
    totalCount: 0,
}

export function useLiveReportsData(studentId: string | number | null | undefined) {
    const dispatch = useDispatch()
    const [cursor, setCursor] = useState<PageCursor>(INITIAL_CURSOR)
    /** Guards FlatList double `onEndReached` before the next render sees `loadingMore`. */
    const loadMoreInFlightRef = useRef(false)

    const req = useSelector(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state: any) => state?.api?.getStudentLiveReports as ApiSlice | undefined
    )

    useEffect(() => {
        if (studentId == null) return
        loadMoreInFlightRef.current = false
        setCursor(INITIAL_CURSOR)
        dispatch(apiRequest(liveReportService.getStudentLiveReports, { id: studentId }, {}, { page: 1 }) as never)
    }, [dispatch, studentId])

    // Mirror legacy PaginatedFlatList: copy meta into local cursor, then idle the slice.
    useEffect(() => {
        if (!req?.success) return
        const meta = req.data?.meta
        if (meta) {
            setCursor({
                currentPage: meta.current_page ?? 1,
                lastPage: meta.last_page ?? 1,
                totalCount: meta.total ?? 0,
            })
        }
        loadMoreInFlightRef.current = false
        dispatch(apiIdle(liveReportService.getStudentLiveReports) as never)
        // `req.data.meta` is destructured into its three primitive fields on purpose — the
        // `meta` object identity changes on every response, which would re-run this effect
        // (and re-`apiIdle`) even when the cursor is unchanged.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, req?.success, req?.data?.meta?.current_page, req?.data?.meta?.last_page, req?.data?.meta?.total])

    useEffect(() => {
        if (!req?.error) return
        loadMoreInFlightRef.current = false
        // A failed page must not leave the cursor claiming more pages exist — the retry
        // re-reads `meta` from the next successful response.
        setCursor((prev) => ({ ...prev, lastPage: prev.currentPage }))
    }, [req?.error])

    const hasMore = cursor.currentPage < cursor.lastPage

    const refresh = () => {
        if (studentId == null) return
        loadMoreInFlightRef.current = false
        setCursor(INITIAL_CURSOR)
        dispatch(apiRequest(liveReportService.getStudentLiveReports, { id: studentId }, {}, { page: 1 }) as never)
    }

    const loadMore = () => {
        if (studentId == null || loadMoreInFlightRef.current || !!req?.request) return
        // `hasMore` is false while an error is pending (lastPage was pinned to currentPage),
        // so the footer retry re-requests the same next page explicitly.
        const nextPage = cursor.currentPage + 1
        if (!hasMore && !req?.error) return
        loadMoreInFlightRef.current = true
        dispatch(
            apiRequest(liveReportService.getStudentLiveReports, { id: studentId }, {}, { page: nextPage }) as never
        )
    }

    return {
        refresh,
        loadMore,
        hasMore,
        totalCount: cursor.totalCount,
    }
}
